import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { Section965Service } from '../../section965.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

/**
 * Section 965 Adjustments (WP) modal.
 * Migrated from section-965-wp-tpl.html + Section965AdjsController.
 */
@Component({
    selector: 'gtw-section-965-adjs',
    templateUrl: './section-965-adjs.component.html',
    styleUrls: ['./section-965-adjs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Section965AdjsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  modalTitle = 'Section 965 Adjustments';

  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};

  ADJ_AMT: number | string = 0;
  COMMENTS = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private section965Service: Section965Service,
    private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.ADJ_AMT = this.rowData.ADJ_AMT ?? 0;
    this.COMMENTS = this.rowData.COMMENTS ?? '';
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const params = {
      ghost_scenario: this.rowData.GHOST_SCENARIO ?? '',
      leid: this.rowData.LEID ?? '',
      tax_code: this.rowData.CDR_NO ?? '',
      filing_group: this.rowData.FILING_GROUP ?? '',
      adj_amt: this.ADJ_AMT,
      comments: this.COMMENTS
    };

    this.section965Service.saveAmtsComments(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(result?.errorMessage || 'Save failed', 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Adjustment Amounts and Comments have been saved', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.ADJ_AMT = this.rowData.ADJ_AMT ?? 0;
    this.COMMENTS = this.rowData.COMMENTS ?? '';
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
